import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PlusIcon from '../../../../icons/PlusIcon';
import { toast } from 'react-toastify';
import { Gig, Milestone } from 'types/collaboration';
import { fetchURLMetadata } from 'services/api/collaboration';
import PasteIcon from 'assets/icons/PasteIcon';
import { Currency } from 'types';
const ChooseFromWebsite: React.FC<{ onSelection: (products: Gig[]) => void, activeMilestone: Milestone, currency: Currency }> = ({ onSelection, activeMilestone, currency }) => {
    const [products, setProducts] = useState<any[]>([]);
    const [fetchingProducts, setFetchingProducts] = useState(false);

    const [clipboardHasText, setClipboardHasText] = useState(false);

    const showAddProductsButton = useMemo(() => {
        const hasPendingUrlEntry = products.some((p) => !p.product);
        return activeMilestone?.gig_type?.slug === 'product_review'
            && activeMilestone?.maximum_gigs > products.length
            && !hasPendingUrlEntry;
    }, [activeMilestone, products]);


    const handleProductChange = (index: number, product: any) => {
        const newProducts = [...products];
        newProducts[index] = product;
        setProducts(newProducts);
    }

    const addProduct = () => {
        let newProducts = [...products];
        newProducts.push({
            url: '',
            editMode: true,
        });
        setProducts(newProducts);
    }

    useEffect(() => {
        if (showAddProductsButton && products.length === 0) {
            addProduct();
        }
    }, [products, showAddProductsButton])

    const checkClipboardHasData = useCallback(async () => {
        let text = '';
        if (navigator.clipboard?.readText) {
            try {
                text = await navigator.clipboard.readText();
            } catch {
                // ignore
            }
        }
        setClipboardHasText(Boolean(text?.trim()));
    }, []);


    const paste = async (index: number) => {
        const product = products[index];
        if (!product) return;
        try {
            let text = '';
            if (!text && navigator.clipboard?.readText) {
                try {
                    text = await navigator.clipboard.readText();
                } catch (err) {
                    console.log(err);
                    toast.info("Couldn't access clipboard. Please paste manually.", { autoClose: 3000, position: 'top-center' });
                }
            }
            setProducts(products.map((product, i) => i === index ? { ...product, url: text } : product));
            setClipboardHasText(Boolean(text?.trim()));
        } catch (error) {
            toast.info(
                "Paste failed. Please try manually.",
                { autoClose: 3000, position: 'top-center' }
            );
            setClipboardHasText(false);
        }
    };

    const fetchProducts = async (index: number) => {
        const product = products[index];
        if (!product?.url) return;
        setFetchingProducts(true);
        try {
            const response = await fetchURLMetadata(product.url);
            if (response.metadata) {
                setProducts(products.map((p, i) => i === index ? {
                    ...p, metadata: response.metadata, product: {
                        name: response.metadata.title ?? '',
                        properties: {
                            description: response.metadata.description ?? '',
                            url: product.url,
                        },
                        thumbnail_url: response.metadata.thumbnail_url ?? '',
                        value: response.metadata.price_amount ?? 0,
                    }
                } : p));
            }
        } catch (error) {
            console.log(error);
        } finally {
            setFetchingProducts(false);
        }
    }

    const removeProduct = (index: number) => {
        let newProducts = [...products];
        newProducts.splice(index, 1);
        setProducts(newProducts);
    }

    useEffect(() => {
        const gigs: Gig[] = products.map((product) => product.product).filter(Boolean) as Gig[];
        onSelection(gigs);
    }, [products]);

    return (
        <div>
            <div></div>
            <div className="product-list d-flex flex-column gap-3">
                {
                    products.map((product, i) => {
                        if (!product.product) {
                            return (
                                <div key={i} className='app-sub-card bg-light-gray p-3'>
                                    <div className="form-group d-flex flex-column gap-2">
                                        <label htmlFor="product-url" className='fs-12 text-dark fw-500'>Product URL</label>
                                        <div className="position-relative">
                                            <div className="input-group mb-3 w-100">
                                                <input type="text" className="form-control form-control-sm form-builder-input border-right-radius-0" style={{ height: 'auto', paddingRight: (clipboardHasText && !product.url) ? '2.8rem' : '0.75rem' }} placeholder="Paste product URL here" aria-label="Paste product URL here" value={product.url} onChange={(e) => handleProductChange(i, { ...product, url: e.target.value })} aria-describedby="basic-addon2" />

                                                {
                                                    clipboardHasText && !product.url && (
                                                        <button type="button" onClick={() => paste(i)} className="position-absolute" style={{ right: '56px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }} aria-label="Paste from clipboard">
                                                            <PasteIcon size={16} />
                                                        </button>
                                                    )
                                                }
                                                <div className="input-group-append">

                                                    <button className="btn btn-primary border-left-radius-0 h-100" type="button" disabled={fetchingProducts} onClick={() => fetchProducts(i)}>
                                                        {
                                                            (fetchingProducts) ? (
                                                                <div className="spinner-border spinner-border-sm" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            ) : (
                                                                <i className="ri-check-line text-white fs-20"></i>
                                                            )
                                                        }
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                        return (
                            <div key={i} className="app-sub-card p-3 d-flex gap-2 align-items-center position-relative">
                                <div className="bg-image gig-image bgc-content" style={{ backgroundImage: `url(${product.product.thumbnail_url})` }}></div>
                                <div className="d-flex flex-column justify-content-center gap-1">
                                    <span className='gig-name fs-12 clamp-1'>{product.product.name}</span>
                                    <div className="d-flex gap-2 align-items-center justify-content-between w-100">
                                        <div className="position-relative">
                                            <span className="position-absolute" style={{ left: '0.75rem', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>{currency?.symbol}</span>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm form-builder-input ps-4"
                                                style={{ height: '1rem', width: '5rem' }}
                                                placeholder="Price" value={product.product.value}
                                                onChange={(e) => handleProductChange(i, { ...product, product: { ...product.product, value: e.target.value } })}
                                            />
                                        </div>
                                        <button className="text-danger unset-all" onClick={() => removeProduct(i)}> <i className="ri-delete-bin-line fs-12"></i></button>
                                    </div>
                                </div>
                            </div>
                        )
                    })
                }
            </div>
            {
                products.length === 0 && (
                    <div className="no-products text-center">
                        <span className='fw-600 fs-14'>No products added!</span>
                    </div>
                )
            }
            {
                showAddProductsButton && (
                    <div className='d-flex justify-content-center mt-2'>
                        <button className='btn btn-link text-primary fs-12 gap-0' disabled={fetchingProducts} onClick={() => addProduct()}> <PlusIcon size={16} /> Add Products</button>
                    </div>
                )
            }
        </div>
    )
}

export default ChooseFromWebsite